import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:8033/api');
const USER_CACHE_KEY = 'userSnapshot';

const resolveMockApi = () => {
  if (process.env.REACT_APP_USE_MOCK_API === 'true') return true;
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem('useMockApi') === 'true') return true;
    const params = new URLSearchParams(window.location.search);
    return params.get('mockApi') === 'true';
  } catch (error) {
    return false;
  }
};

const useMockApi = resolveMockApi();

const normalizeDescriptionWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const looksGenericDescription = (value) => {
  const normalized = normalizeDescriptionWhitespace(value).toLowerCase();
  return (
    !normalized ||
    normalized === 'description' ||
    normalized === 'description:' ||
    normalized === 'dataset description' ||
    normalized === 'dataset description:'
  );
};

const toCrispDescription = (value, fallback = 'Indexed dataset entry') => {
  let text = normalizeDescriptionWhitespace(value);
  if (!text || looksGenericDescription(text)) return fallback;

  text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi, '$1');
  text = text.replace(/<[^>]+>/g, ' ');
  text = text.replace(/https?:\/\/\S+/gi, ' ');
  text = text.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, ' ');
  text = text.replace(/\[!?\s*note\s*\][^.!?]*[.!?]?/gi, ' ');
  text = text.replace(/please\s+help\s+us\s+protect[^.!?]*[.!?]?/gi, ' ');
  text = text.replace(/not\s+publicly\s+sharing[^.!?]*[.!?]?/gi, ' ');
  text = text.replace(/re-?uploading[^.!?]*[.!?]?/gi, ' ');
  text = text.replace(/distributing\s+the\s+dataset[^.!?]*[.!?]?/gi, ' ');
  text = text.replace(/\b(?:website|paper|github(?:\s+center)?|dataset card|documentation|leaderboard)\b/gi, ' ');
  text = normalizeDescriptionWhitespace(text.replace(/\s*\|\s*/g, '. '));

  const sentence = text.split(/(?<=[.!?])\s+/).find((item) => item && item.length > 24) || text;
  const concise = normalizeDescriptionWhitespace(
    sentence.split(/(?:,\s*designed\s+to|\s+designed\s+to)/i)[0]
  );
  if (looksGenericDescription(concise)) return fallback;
  if (concise.length <= 140) return concise;
  const cut = concise.slice(0, 140).replace(/\s+\S*$/, '').trim();
  return `${cut || concise.slice(0, 140).trim()}...`;
};

const withCrispDatasetDescription = (dataset = {}) => ({
  ...dataset,
  description: toCrispDescription(dataset.description, dataset.name || 'Indexed dataset entry'),
});

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem(USER_CACHE_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const sanitizeBenchmarkPayload = (benchmark = {}) => ({
  ...benchmark,
  dataset: benchmark.dataset ? withCrispDatasetDescription(benchmark.dataset) : benchmark.dataset,
});

const sanitizeDatasetsResponse = (payload = {}) => ({
  ...payload,
  datasets: (payload.datasets || []).map((dataset) => withCrispDatasetDescription(dataset)),
});

const sanitizeEntitlements = (items = []) =>
  items.map((item) => ({
    ...item,
    dataset: item.dataset ? withCrispDatasetDescription(item.dataset) : item.dataset,
  }));

const realAuthAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
};

const realDatasetsAPI = {
  getAll: (params) => api.get('/datasets', { params }).then((payload) => sanitizeDatasetsResponse(payload)),
  getById: (id) =>
    api.get(`/datasets/${id}`).then((payload) => ({
      ...withCrispDatasetDescription(payload),
      benchmarkResults: (payload.benchmarkResults || []).map((benchmark) =>
        sanitizeBenchmarkPayload(benchmark)
      ),
    })),
  getSamples: (id, params) => api.get(`/datasets/${id}/samples`, { params }),
  getDataDictionary: (id, params) => api.get(`/datasets/${id}/data-dictionary`, { params }),
  getScriptPack: (id, params) => api.get(`/datasets/${id}/script-pack`, { params }),
  getAccessProfile: (id, params) => api.get(`/datasets/${id}/access`, { params }),
  trackEngagement: (id, payload) => api.post(`/datasets/${id}/engagement`, payload),
  reportConcern: (id, payload) => api.post(`/datasets/${id}/report`, payload),
  getBenchmarks: (id) =>
    api
      .get(`/datasets/${id}/benchmarks`)
      .then((benchmarks) => (benchmarks || []).map((benchmark) => sanitizeBenchmarkPayload(benchmark))),
  getStats: (id) => api.get(`/datasets/${id}/stats`),
  create: (data) => api.post('/datasets', data),
  update: (id, data) => api.put(`/datasets/${id}`, data),
  delete: (id) => api.delete(`/datasets/${id}`),
};

const realModelsAPI = {
  getAll: (params) => api.get('/models', { params }),
  getById: (id) => api.get(`/models/${id}`),
  getPerformance: (id) => api.get(`/models/${id}/performance`),
  getBenchmarksByModel: (id) => api.get(`/models/${id}/benchmarks`),
};

const realBenchmarksAPI = {
  getAll: (params) =>
    api.get('/benchmarks', { params }).then((payload) => ({
      ...payload,
      benchmarks: (payload.benchmarks || []).map((benchmark) => sanitizeBenchmarkPayload(benchmark)),
    })),
  getById: (id) => api.get(`/benchmarks/${id}`).then((payload) => sanitizeBenchmarkPayload(payload)),
  reportConcern: (id, payload) => api.post(`/benchmarks/${id}/report`, payload),
  compare: (datasetId, modelIds) =>
    api.get(`/benchmarks/compare/${datasetId}`, {
      params: { modelIds: modelIds.join(',') },
    }).then((payload) => ({
      ...payload,
      dataset: payload.dataset ? withCrispDatasetDescription(payload.dataset) : payload.dataset,
      benchmarks: (payload.benchmarks || []).map((benchmark) => sanitizeBenchmarkPayload(benchmark)),
    })),
  leaderboard: (industry, params) =>
    api
      .get(`/benchmarks/leaderboard/${industry}`, { params })
      .then((benchmarks) => (benchmarks || []).map((benchmark) => sanitizeBenchmarkPayload(benchmark))),
};

const realMarketplaceAPI = {
  getDatasets: (params) =>
    api.get('/marketplace/datasets', { params }).then((payload) => sanitizeDatasetsResponse(payload)),
  getFacets: () => api.get('/marketplace/facets'),
  getDataset: (id) => api.get(`/marketplace/datasets/${id}`).then((payload) => withCrispDatasetDescription(payload)),
  purchase: (datasetId) => api.post(`/marketplace/purchase/${datasetId}`),
  getMyDatasets: () =>
    api.get('/marketplace/my-datasets').then((payload) => sanitizeEntitlements(payload)),
  getUserPurchases: () =>
    api.get('/marketplace/my-purchases').then((payload) => sanitizeEntitlements(payload)),
  download: (datasetId) => api.get(`/marketplace/download/${datasetId}`),
};

const realCatalogAPI = {
  getSummary: () => api.get('/catalog/summary'),
};

const realDashboardAPI = {
  getDashboardStats: () => api.get('/dashboard/stats'),
  getUserActivity: () => api.get('/dashboard/activity'),
  getVendorDashboard: () => api.get('/vendor/dashboard'),
  getBuyerIntel: () => api.get('/vendor/buyer-intel'),
};

const realAccountAPI = {
  getSummary: () =>
    api.get('/account/summary').then((payload) => ({
      ...payload,
      purchasedDatasets: sanitizeEntitlements(payload.purchasedDatasets || []),
    })),
  getNotifications: () => api.get('/account/notifications'),
  markNotificationRead: (id) => api.post(`/account/notifications/${id}/read`),
  markAllNotificationsRead: () => api.post('/account/notifications/read-all'),
  updateSettings: (payload) => api.patch('/account/settings', payload),
  updateWorkspace: (payload) => api.patch('/account/workspace', payload),
  addWorkspaceMember: (payload) => api.post('/account/workspace/members', payload),
  updateWorkspaceMember: (memberId, payload) => api.patch(`/account/workspace/members/${memberId}`, payload),
  createProcurementRequest: (payload) => api.post('/account/procurement-requests', payload),
  approveProcurementRequest: (requestId, payload) =>
    api.post(`/account/procurement-requests/${requestId}/approve`, payload),
  assignEntitlementSeat: (purchaseId, payload) =>
    api.post(`/account/entitlements/${purchaseId}/assign-seat`, payload),
  revokeEntitlement: (purchaseId, payload) =>
    api.post(`/account/entitlements/${purchaseId}/revoke`, payload),
};

const realStripeAPI = {
  createPaymentIntent: (payload) => api.post('/stripe/payment-intents', payload),
  confirmPayment: (payload) => api.post('/stripe/confirm', payload),
};

const realRequestsAPI = {
  create: (data) => api.post('/requests', data),
  getOpenCalls: (params) => api.get('/requests/calls', { params }),
  getCall: (requestId) => api.get(`/requests/calls/${requestId}`),
  createCallInquiry: (requestId, question) => api.post(`/requests/calls/${requestId}/inquiries`, { question }),
  answerCallInquiry: (requestId, inquiryId, response) => api.patch(`/requests/calls/${requestId}/inquiries/${inquiryId}`, { response }),
  createFulfillment: (requestId, payload) => {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (key === 'files') (value || []).forEach((file) => formData.append('files', file));
      else if (key === 'dynamicFiles') Object.entries(value || {}).forEach(([fieldKey, files]) => (files || []).forEach((file) => formData.append(`dynamic_${fieldKey}`, file)));
      else if (key === 'dynamicResponses') formData.append(key, JSON.stringify(value || []));
      else if (key === 'dynamicValues') return;
      else if (value !== undefined && value !== null) formData.append(key, value);
    });
    return api.post(`/requests/calls/${requestId}/fulfill`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateSubmissionPage: (requestId, payload) => api.patch(`/requests/calls/${requestId}/submission-page`, payload),
  uploadSubmissionPageDocuments: (requestId, files, metadata = {}) => {
    const formData = new FormData();
    (files || []).forEach((file) => formData.append('documents', file));
    Object.entries(metadata).forEach(([key, value]) => formData.append(key, value));
    return api.post(`/requests/calls/${requestId}/submission-page/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadCallDocument: (requestId, documentId) => api.get(`/requests/calls/${requestId}/documents/${documentId}`, { responseType: 'blob' }),
  reviewSubmission: (requestId, submissionId, payload) => api.patch(`/requests/calls/${requestId}/submissions/${submissionId}`, payload),
  downloadSubmissionFile: (requestId, submissionId, fileId) => api.get(`/requests/calls/${requestId}/submissions/${submissionId}/files/${fileId}`, { responseType: 'blob' }),
  getMyRequests: () => api.get('/requests/my'),
};

const realSearchAPI = {
  smart: (params) => api.get('/search/smart', { params }),
};

const realAnnotatorAPI = {
  getStatus: () => api.get('/annotator/status'),
  createSession: (returnTo = '/') => api.post('/annotator/session', { returnTo }),
  getProjects: () => api.get('/annotator/projects'),
  createProject: (payload) => api.post('/annotator/projects', payload),
  sync: (id) => api.post(`/annotator/projects/${id}/sync`),
  updateMarketplace: (id, payload) => api.patch(`/annotator/projects/${id}/marketplace`, payload),
  publish: (id, payload) => api.post(`/annotator/projects/${id}/publish`, payload),
  unpublish: (id, payload) => api.post(`/annotator/projects/${id}/unpublish`, payload),
  importDataset: (datasetId) => api.post(`/annotator/import/${datasetId}`),
};

const realTasksAPI = {
  getTasks: (params = {}) => api.get('/tasks', { params }),
  getTask: (id) => api.get(`/tasks/${id}`),
  create: (payload) => api.post('/tasks', payload),
  submit: (id, payload) => api.post(`/tasks/${id}/contributions`, { payload }),
  mySummary: () => api.get('/tasks/summary/mine'),
  launch: (id) => api.post(`/tasks/${id}/launch`),
  sponsoredMine: () => api.get('/tasks/sponsored/mine'),
  rows: (id) => api.get(`/tasks/${id}/rows`),
  exportRows: (id, format = 'json') => api.get(`/tasks/${id}/export`, { params: { format }, responseType: 'blob' }),
  getInvitations: (id) => api.get(`/tasks/${id}/invitations`),
  invite: (id, payload) => api.post(`/tasks/${id}/invitations`, payload),
  respondInvitation: (id, decision) => api.post(`/tasks/${id}/invitations/respond`, { decision }),
  reviewSubmission: (id, subId, decision, note = '') => api.post(`/tasks/${id}/submissions/${subId}/review`, { decision, note }),
  history: (params = {}) => api.get('/tasks/history/mine', { params }),
  annotatorAnalytics: () => api.get('/tasks/analytics/annotator'),
  buyerAnalytics: () => api.get('/tasks/analytics/buyer'),
  myWorkbench: () => api.get('/tasks/workbench/mine'),
  reviewAll: (id, decision) => api.post(`/tasks/${id}/submissions/review-all`, { decision }),
  // Mercor-style gated application flow
  getApplication: (id) => api.get(`/tasks/${id}/application`),
  applicationPhoneStart: (id, payload) => api.post(`/tasks/${id}/application/phone/start`, payload),
  applicationPhoneVerify: (id, code) => api.post(`/tasks/${id}/application/phone/verify`, { code }),
  applicationResume: (id, payload) => api.post(`/tasks/${id}/application/resume`, payload),
  applicationWorkAuth: (id, payload) => api.post(`/tasks/${id}/application/work-auth`, payload),
  applicationScreening: (id, responses) => api.post(`/tasks/${id}/application/screening`, { responses }),
  applicationSubmit: (id) => api.post(`/tasks/${id}/application/submit`),
  getApplications: (id) => api.get(`/tasks/${id}/applications`),
  decideApplication: (id, appId, decision, note = '') => api.post(`/tasks/${id}/applications/${appId}/decision`, { decision, note }),
};

const realTalentAPI = {
  list: (params = {}) => api.get('/talent', { params }),
  get: (id) => api.get(`/talent/${id}`),
  getMe: () => api.get('/talent/me'),
  updateMe: (payload) => api.patch('/talent/me', payload),
};
export const talentAPI = realTalentAPI;

const realDemandAPI = {
  list: () => api.get('/demand'),
  propose: (id, payload) => api.post(`/demand//proposals`, payload),
  createCategory: (payload) => api.post('/demand', payload),
  proposals: () => api.get('/demand/proposals'),
};
export const demandAPI = realDemandAPI;

const realAdminAPI = {
  getDatasets: (params) => api.get('/admin/datasets', { params }),
  getBenchmarks: (params) => api.get('/admin/benchmarks', { params }),
  getDashboard: () => api.get('/admin/dashboard'),
  getExternalSyncStatus: () => api.get('/admin/external-sync/status'),
  runExternalSync: () => api.post('/admin/external-sync/run'),
  getConcernReports: (params) => api.get('/admin/concern-reports', { params }),
  updateConcernReport: (id, payload) => api.patch(`/admin/concern-reports/${id}`, payload),
  deleteTestSeedDatasets: (tag = 'ui-test-seed') =>
    api.delete('/admin/datasets/test-seeds', { params: { tag } }),
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (id, role) => api.patch(`/admin/users/${id}/role`, { role }),
  updateUserApproval: (id, payload) => api.patch(`/admin/users/${id}/approval`, payload),
  updateUserModules: (id, moduleAccess) => api.patch(`/admin/users/${id}/modules`, { moduleAccess }),
  getCallForDataRequests: (params) => api.get('/admin/call-for-data', { params }),
  updateCallForDataRequest: (id, payload) => api.patch(`/admin/call-for-data/${id}`, payload),
  getCallForDataFulfillments: (params) => api.get('/admin/call-for-data/fulfillments', { params }),
  updateCallForDataFulfillment: (id, payload) => api.patch(`/admin/call-for-data/fulfillments/${id}`, payload),
  saveBenchmarkEvidence: (payload) => api.post('/admin/benchmarks/evidence', payload),
  importBenchmarks: (benchmarks) => api.post('/admin/benchmarks/import', { benchmarks }),
  saveDatasetDraft: (id, payload) =>
    id ? api.patch(`/admin/datasets/${id}/draft`, payload) : api.post('/admin/datasets', payload),
  submitDatasetReview: (id, payload) => api.post(`/admin/datasets/${id}/review`, payload),
  publishDataset: (id, payload) => api.post(`/admin/datasets/${id}/publish`, payload),
  rollbackDataset: (id, payload) => api.post(`/admin/datasets/${id}/rollback`, payload),
  getDatasetVersions: (id) => api.get(`/admin/datasets/${id}/versions`),
  getDatasetDiff: (id, params) => api.get(`/admin/datasets/${id}/diff`, { params }),
  toggleDatasetVisibility: (id, visibility) =>
    api.patch(`/admin/datasets/${id}/visibility`, visibility),
  previewDatasetUpload: (file, payload, schemaMapping = {}) => {
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }
    formData.append('payload', JSON.stringify(payload || {}));
    formData.append('schemaMapping', JSON.stringify(schemaMapping || {}));
    return api.post('/upload/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  previewDataBuilder: (file, payload, schemaMapping = {}, prompt = '') => {
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }
    formData.append('payload', JSON.stringify(payload || {}));
    formData.append('schemaMapping', JSON.stringify(schemaMapping || {}));
    formData.append('prompt', prompt || '');
    return api.post('/upload/builder/sample', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  runDataBuilder: (file, payload, schemaMapping = {}, prompt = '', validatedRows = []) => {
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }
    formData.append('payload', JSON.stringify(payload || {}));
    formData.append('schemaMapping', JSON.stringify(schemaMapping || {}));
    formData.append('validatedRows', JSON.stringify(validatedRows || []));
    formData.append('prompt', prompt || '');
    return api.post('/upload/builder/run', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  queueDatasetUpload: ({ datasetId, file, payload, schemaMapping = {} }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (datasetId) {
      formData.append('datasetId', datasetId);
    }
    formData.append('payload', JSON.stringify(payload || {}));
    formData.append('schemaMapping', JSON.stringify(schemaMapping || {}));
    return api.post('/upload/jobs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getIngestionJob: (jobId) => api.get(`/upload/jobs/${jobId}`),
  retryIngestionJob: (jobId) => api.post(`/upload/jobs/${jobId}/retry`),
};

let mockApiModulePromise;

const loadMockApiModule = () => {
  if (!mockApiModulePromise) {
    mockApiModulePromise = import('./mockApi');
  }
  return mockApiModulePromise;
};

const createRuntimeApi = (realApi, exportName) =>
  new Proxy(realApi, {
    get(target, prop) {
      const targetValue = target[prop];
      if (!useMockApi || typeof targetValue !== 'function') {
        return targetValue;
      }
      return (...args) =>
        loadMockApiModule().then((module) => {
          const runtimeApi = module[exportName];
          if (!runtimeApi || typeof runtimeApi[prop] !== 'function') {
            throw new Error(`Mock API method ${exportName}.${String(prop)} is not available`);
          }
          return runtimeApi[prop](...args);
        });
    },
  });

export const authAPI = createRuntimeApi(realAuthAPI, 'authAPI');
export const datasetsAPI = createRuntimeApi(realDatasetsAPI, 'datasetsAPI');
export const modelsAPI = createRuntimeApi(realModelsAPI, 'modelsAPI');
export const benchmarksAPI = createRuntimeApi(realBenchmarksAPI, 'benchmarksAPI');
export const marketplaceAPI = createRuntimeApi(realMarketplaceAPI, 'marketplaceAPI');
export const catalogAPI = createRuntimeApi(realCatalogAPI, 'catalogAPI');
export const dashboardAPI = createRuntimeApi(realDashboardAPI, 'dashboardAPI');
export const accountAPI = createRuntimeApi(realAccountAPI, 'accountAPI');
export const stripeAPI = createRuntimeApi(realStripeAPI, 'stripeAPI');
export const requestsAPI = createRuntimeApi(realRequestsAPI, 'requestsAPI');
export const searchAPI = createRuntimeApi(realSearchAPI, 'searchAPI');
export const adminAPI = createRuntimeApi(realAdminAPI, 'adminAPI');
export const annotatorAPI = createRuntimeApi(realAnnotatorAPI, 'annotatorAPI');
export const tasksAPI = realTasksAPI;
export const isUsingMockApi = useMockApi;

export default api;
